/**
 * What the app says when this phone's copy of the book may be gone: the
 * browser wiped it under the page (`OutboxUnavailableError`, SB-GON), or a
 * newer version took it over and this page cannot rule out a wipe as well
 * (`OutboxSupersededError` with `storeMayBeGone`).
 *
 * One builder for every surface, and every sentence it can produce is true in
 * every branch: whether or not the store really was cleared, whether or not the
 * game is finished, and whether or not this phone can carry on after the
 * reload. So it states what is known (what the server did, what was not
 * recorded), says where unsent plays may now be, and gives the one next step
 * that is always right, the reload. It never promises what the reload brings
 * back, never says the plays are saved, and never offers "Try again": every
 * later call refuses until the reload. After the reload this phone may not be
 * able to carry on a live game, so every body ends with one conditional step
 * (`NEXT_STEP`) that is true in every state: finished or not, reopened or not,
 * and whether or not the store was really cleared.
 *
 * Earlier versions picked a sentence per case ("resume it", "keep scoring",
 * "keep the rest of the game on paper", "record the missing ones again"), and
 * each fix round found another case where one of them was false.
 */

export const UNAVAILABLE_TITLE = 'The browser cleared the scorebook on this phone.';

/** Something known to have happened, said once each, in the order first seen. */
export type LostFact =
  /** Finish reached the server; only this phone's copy failed. */
  | 'finished'
  /** Reopen reached the server; only this phone's copy failed. */
  | 'reopened'
  /** A Finish answered in a way that does not confirm it either way (a 5xx, an unreadable 200). */
  | 'mayHaveFinished'
  /** The same for a Reopen. */
  | 'mayHaveReopened'
  /** Start reached the server, so the game exists there. */
  | 'created'
  /** A tap on this page was refused. It may or may not be on disk: a retried write can have committed first. */
  | 'refused'
  /** Start sent nothing to the server. */
  | 'notStarted'
  /** The setup form could not be saved, so what is on it may be lost. */
  | 'formUnsaved';

const FACT: Readonly<Record<LostFact, string>> = {
  finished: 'The game was finished on the server.',
  reopened: 'The game was reopened on the server.',
  mayHaveFinished: 'The game may have been finished on the server.',
  mayHaveReopened: 'The game may have been reopened on the server.',
  created: 'The game was created on the server.',
  refused:
    'An entry made on this page may not have been recorded. After the reload, check the book before entering it again.',
  notStarted: 'This Start sent nothing to the server.',
  formUnsaved: 'Anything entered on this form may need to be entered again.',
};

/**
 * The refused fact once the game is or may be final on the server: no step to
 * enter it again. It says nothing about WHEN the entry was made: the facts are
 * kept in the order the page heard them, which is not the order they happened.
 */
export const REFUSED_AFTER_FINISH = 'An entry made on this page may not have been recorded.';

/** Whether any fact says the game is, or may be, final on the server. */
export function mayBeFinal(facts: readonly string[]): boolean {
  return facts.includes('finished') || facts.includes('mayHaveFinished');
}

/**
 * The one conditional next step, in every body. It is true whether the game is
 * finished, live or reopened, and whether or not the store was really cleared.
 */
export const NEXT_STEP =
  'If the game is still being played and does not come back after the reload, score the rest of it on paper.';

const ON_SCREEN =
  'Plays that had not reached the server may now be only on this screen. Write them down before you reload.';
const OFF_SCREEN = 'Plays that had not reached the server may no longer be on this phone.';

/**
 * The body, without a reference: a dialog carries its diagnostic separately,
 * and a notice appends it.
 *
 * `bookOnScreen` is whether the live book is showing, and so whether there is a
 * screen to write unsent plays down from. `finalOnServer` is whether the game
 * is or may be final on the server, which drops the step to enter a refused
 * entry again; it defaults to what the facts imply.
 */
export function lostBody(
  facts: readonly LostFact[],
  bookOnScreen: boolean,
  finalOnServer: boolean = mayBeFinal(facts),
): string {
  const distinct = facts.filter((fact, index) => facts.indexOf(fact) === index);
  // Once the game is final on the server, entering a play into it is wrong,
  // whichever order the two were seen in (the same rule `SupersededNotice`
  // applies to its refused sentence), so the refused sentence stops steering
  // the coach to re-enter it.
  const said = distinct.map((fact) =>
    fact === 'refused' && finalOnServer ? REFUSED_AFTER_FINISH : FACT[fact],
  );
  return [
    ...said,
    bookOnScreen ? ON_SCREEN : OFF_SCREEN,
    bookOnScreen ? 'Then reload this page.' : 'Reload this page.',
    NEXT_STEP,
  ].join(' ');
}

/** The short form for the sync indicator, which only renders over the live book. */
export const LOST_LINE = 'Write down the plays on this screen before you reload.';

/**
 * The one-line form of the wipe notice over the live book, for a screen with no
 * room for the full body (ADR-0083, ADR-0086). It says what happened, where the
 * unsent plays may be and the step before the reload; the reload is the button
 * beside it and the reference is shown with it. It claims nothing the full body
 * does not: it is an instruction, not a statement about what was kept. When a tap
 * was refused, `LOST_PLAY_SHORT` takes its place (ADR-0083 item 5).
 */
export const LOST_SHORT = 'Cleared. Write down this screen.';

/**
 * The one-line form over a store that may be lost (a wipe, or a takeover that
 * cannot rule one out) when a tap was refused: the play that may be lost, then
 * the write-down step. The refused play is not on this screen, so it is named.
 * A question, because it may have landed: the entry fact is sticky and also
 * follows an uncertain write. "the last play" would be false for the same
 * reason. It names no repeat; the full text has the sequence.
 */
export const LOST_PLAY_SHORT = 'Play lost? Write down this screen.';

/**
 * The one precedence rule between the two stops, for every screen.
 *
 * Given every stop a screen has heard (from a refused tap, a failed open, or
 * the sync status), in any order, a wipe wins over a takeover. A wipe is
 * known; a takeover only says the store may be intact. A stop heard first as
 * superseded can turn out to be a wipe (db.ts moves the stop on), and the
 * reverse never happens, so the screen must never keep the first it heard.
 */
export function winningStop(
  heard: readonly ('superseded' | 'unavailable' | null)[],
): 'superseded' | 'unavailable' | null {
  if (heard.includes('unavailable')) return 'unavailable';
  if (heard.includes('superseded')) return 'superseded';
  return null;
}
